import BigNumber from 'bignumber.js'
import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import unescape from 'lodash/unescape'
import { Blockchain } from '@/constants/blockchain'
import { ContractVersion } from '@/constants/contracts'
import { BACKEND_CURRENCIES_BY_ID, CURRENCY_FIELD_BACKEND_ID } from '@/constants/currency'
import { GigJobStages } from '@/constants/gig/gigJobStages'
import { GigOfferStages } from '@/constants/gig/gigOfferStages'
import { GigStatuses } from '@/constants/gig/gigStatuses'
import { GigTimeTypes } from '@/constants/gig/gigTimeTypes'
import { CustomerTypes } from '@/constants/user/roles'
import { DATE_TIME_FORMAT_BY_MERIDIEM } from '@/constants/utils/date'
import CommonProfile from '@/models-ts/user/info/CommonProfile'
import { getCurrency } from '@/utils-ts/currencies'
import { convertToLocal } from '@/utils/date'
import { getUrl } from '@/utils/file'
import { parseSlug } from '@/utils/parser'
import Wallet from '../Wallet'

export default class GigOfferListItem {
  id: number
  sc_id: string
  gig_id: number
  stage: GigOfferStages
  customerId: number
  freelancerId: number
  description: string | null
  currency: string | null
  contractVersion: ContractVersion
  rate: string
  slug: string
  hours: number | string | null
  gigStatus: GigStatuses
  blockchain?: Blockchain | null
  name: string
  preferredCurrencies: Array<{ id: number; name: string }> | null
  isDone: boolean
  isRemoved: boolean
  customerLeftReview: boolean
  freelancerLeftReview: boolean
  deadline: number | null
  time_type: GigTimeTypes
  applicationId: number | null
  customerProfile: CommonProfile | null
  freelancerProfile: CommonProfile | null
  freelancer_wallet?: string | null
  customerWallet: string | null
  createdAt: string
  document: string | null
  sortField: string
  customerType: CustomerTypes | null
  job: {
    id: number
    escrow_balance: BigNumber
    txid_completed?: string | null
    txid_created?: string | null
    stage: GigJobStages
    created_at: string
  } | null

  constructor(data: Partial<GigOfferListItem>) {
    Object.assign(this, cloneDeep(data))
  }

  static fromServer(data: LX.GigOfferDetails) {
    if (!data) {
      return null
    }
    const gigJob = data.relations.GigJob
    const currencyBackendId = data.currency || gigJob?.currency
    const currency = getCurrency({
      blockchain: data.blockchain || gigJob?.blockchain || undefined,
      value: currencyBackendId || null,
      field: CURRENCY_FIELD_BACKEND_ID,
    })
    const gig = data.relations.Gig
    let reviews: LX.UserReview[] = []
    if (gigJob?.relations?.Review) {
      reviews = Array.isArray(gigJob?.relations?.Review) ? gigJob?.relations?.Review : [gigJob?.relations?.Review]
    } else if (data.relations?.Review) {
      reviews = Array.isArray(data.relations?.Review) ? data.relations?.Review : [data.relations?.Review]
    }

    return new GigOfferListItem({
      id: data.id,
      gig_id: data.gig_id,
      sc_id: data.sc_id,
      name: unescape(gig.name || ''),
      description: data.description || '',
      rate: String(data.rate || gig.rate || '0.00'),
      stage: data.stage,
      freelancerLeftReview: reviews.some((item) => item?.from_user_id === data.freelancer_id),
      customerLeftReview: reviews.some((item) => item?.from_user_id === data.customer_id),
      time_type: Number(data?.time_type || 0) || Number(gig.time_type || 0) || GigTimeTypes.FIXED,
      sortField: data.updated_at || data.created_at,
      contractVersion: data.relations?.GigJob?.contract_version || ContractVersion.v1,
      slug: parseSlug(gig.slug),
      blockchain: data?.blockchain || data.relations?.GigJob?.blockchain,
      hours: String(data.hours || data.time_value || gig.time_value) || null,
      deadline: +Number(data.deadline || 0) || +gig.deadline || null,
      gigStatus: +gig.status,
      isRemoved: Boolean(gig.is_removed),
      createdAt: convertToLocal(data.created_at, DATE_TIME_FORMAT_BY_MERIDIEM),
      currency: Number(currencyBackendId) ? BACKEND_CURRENCIES_BY_ID[Number(currencyBackendId)] : null,
      preferredCurrencies: gig.relations.Currency || [],
      applicationId: data.gig_application_id,
      freelancerId: data.freelancer_id,
      customerId: data.customer_id,
      customerProfile: data.relations.Customer ? CommonProfile.fromServer(data.relations.Customer) : null,
      freelancerProfile: data.relations.Freelancer ? CommonProfile.fromServer(data.relations.Freelancer) : null,
      freelancer_wallet: data?.freelancer_wallet,
      customerWallet: get(data, 'relations.GigJob.customer_wallet', null),
      document: data.relations?.File?.id ? getUrl(data.relations.File) : null,
      isDone: Boolean(get(data, 'relations.GigJob.is_done', false)),
      job: ((gigJob?: LX.GigJob | null) => {
        if (!gigJob || Array.isArray(gigJob)) return null
        return {
          id: gigJob.id,
          txid_completed: gigJob.txid_completed,
          txid_created: gigJob.txid_created,
          stage: gigJob.stage,
          created_at: gigJob.created_at,
          escrow_balance: new BigNumber(gigJob.escrow_balance).dividedBy(currency?.baseUnits || 1),
        }
      })(gigJob),
      customerType: get(data, 'relations.Customer.profile.type', null),
    })
  }
}
